import React, { createContext, useContext, useState, useEffect, useRef } from 'react'
import axios from 'axios'
import config from '../config'
import { toast } from 'react-toastify'

const FocusContext = createContext(null)

export const FOCUS_MUSIC_URLS = [
  { name: 'Deep Focus & Energy Boost', url: 'https://kael.blob.core.windows.net/axoplan/Deep Focus & Energy Boost.mp3' },
  { name: 'Lofi Study', url: 'https://kael.blob.core.windows.net/axoplan/Lofi Study.mp3' },
  { name: 'AC/DC - Back In Black', url: 'https://kael.blob.core.windows.net/axoplan/ACDC%20-%20Back%20In%20Black%20(Official%20Video).mp3' },
  { name: 'AC/DC - Thunderstruck', url: 'https://kael.blob.core.windows.net/axoplan/ACDC%20-%20Thunderstruck%20(Official%20Video).mp3' },
  { name: 'KALEO - Break My Baby', url: 'https://kael.blob.core.windows.net/axoplan/KALEO%20-%20Break%20My%20Baby%20%5BOFFICIAL%20LYRIC%20VIDEO%5D.mp3' },
  { name: 'KALEO - No Good', url: 'https://kael.blob.core.windows.net/axoplan/KALEO%20-%20No%20Good%20(Live%20at%20United%20Record%20Pressing).mp3' },
  { name: 'KALEO - Broken Bones', url: 'https://kael.blob.core.windows.net/axoplan/KALEO%20Broken%20Bones%20%5BOfficial%20Audio%5D.mp3' },
  { name: 'Man of Steel - Hans Zimmer', url: 'https://kael.blob.core.windows.net/axoplan/Man%20of%20Steel%20Soundtrack%20%20What%20Are%20You%20Going%20to%20Do%20When%20You%20Are%20Not%20Saving%20the%20World_%20-%20Hans%20Zimmer.mp3' },
  { name: 'Succession Theme', url: 'https://kael.blob.core.windows.net/axoplan/Succession%20(Main%20Title%20Theme)%20-%20Nicholas%20Britell%20%20Succession%20(HBO%20Original%20Series%20Soundtrack).mp3' },
  { name: 'The White Stripes - Seven Nation Army', url: 'https://kael.blob.core.windows.net/axoplan/The%20White%20Stripes%20-%20Seven%20Nation%20Army%20(Official%20Music%20Video).mp3' },
]

export const getYoutubeId = (url) => {
  if (!url) return null
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})/)
  return match ? match[1] : null
}

export const getYoutubeInfo = (url) => {
  if (!url) return { videoId: null, listId: null }
  
  let videoId = null
  let listId = null
  
  const vMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})/)
  if (vMatch) videoId = vMatch[1]
  
  const lMatch = url.match(/[?&]list=([^#\&\?]+)/)
  if (lMatch) listId = lMatch[1]
  
  return { videoId, listId }
}

export const FocusProvider = ({ children }) => {
  // Timer State
  const [focusTimer, setFocusTimer] = useState(120)
  const [breakTimer, setBreakTimer] = useState(20)
  const [isTimerRunning, setIsTimerRunning] = useState(false)
  const [isBreakTime, setIsBreakTime] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [activeTaskId, setActiveTaskId] = useState(null)
  
  // Tracking
  const [currentSessionId, setCurrentSessionId] = useState(null)

  // Music State
  const [selectedMusicUrl, setSelectedMusicUrl] = useState(FOCUS_MUSIC_URLS[0].url)
  const [currentTrackName, setCurrentTrackName] = useState(FOCUS_MUSIC_URLS[0].name)
  const [currentTrackCover, setCurrentTrackCover] = useState(null)
  const [isMusicEnabled, setIsMusicEnabled] = useState(true)
  const [useYoutube, setUseYoutube] = useState(false)
  const [youtubeUrl, setYoutubeUrl] = useState('')

  // Enhanced Audio State
  const [isPlaying, setIsPlaying] = useState(false)
  const [audioProgress, setAudioProgress] = useState(0)
  const [audioDuration, setAudioDuration] = useState(0)
  
  const [isShuffle, setIsShuffle] = useState(false)
  const [isRepeat, setIsRepeat] = useState(false)
  const [playlistQueue, setPlaylistQueue] = useState([])
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0)
  const [ytTitles, setYtTitles] = useState({})

  // Volume 0–100 and mute, shared by the built-in tracks and YouTube; remembered per browser
  const readStored = (key, fallback) => {
    try {
      const v = localStorage.getItem(key)
      return v === null ? fallback : JSON.parse(v)
    } catch (e) {
      return fallback
    }
  }
  const [volume, setVolumeState] = useState(() => {
    const v = Number(readStored('musicVolume', 30))
    return Number.isFinite(v) ? Math.min(100, Math.max(0, v)) : 30
  })
  const [isMuted, setIsMuted] = useState(() => Boolean(readStored('musicMuted', false)))

  const timerRef = useRef(null)
  const audioRef = useRef(null)
  
  // Custom YouTube API refs
  const ytPlayerRef = useRef(null)
  const ytIntervalRef = useRef(null)

  // Audio lifecycle
  useEffect(() => {
    if (!useYoutube && selectedMusicUrl) {
      audioRef.current = new Audio(selectedMusicUrl)
      audioRef.current.loop = isRepeat
      audioRef.current.volume = isMuted ? 0 : volume / 100
      
      setPlaylistQueue(FOCUS_MUSIC_URLS.map((t, i) => ({ id: t.url, index: i, name: t.name, cover: null })))
      const cIndex = FOCUS_MUSIC_URLS.findIndex(t => t.url === selectedMusicUrl)
      setCurrentTrackIndex(cIndex >= 0 ? cIndex : 0)

      const handleTrackEnd = () => {
        if (isMusicEnabled) {
          if (isRepeat) {
            audioRef.current.currentTime = 0
            audioRef.current.play()
            return
          }
          let nextIndex
          if (isShuffle) {
            nextIndex = Math.floor(Math.random() * FOCUS_MUSIC_URLS.length)
          } else {
            nextIndex = (cIndex + 1) % FOCUS_MUSIC_URLS.length
          }
          const nextTrackItem = FOCUS_MUSIC_URLS[nextIndex]
          setSelectedMusicUrl(nextTrackItem.url)
          setCurrentTrackName(nextTrackItem.name)
          setCurrentTrackCover(null)
          setCurrentTrackIndex(nextIndex)
        }
      }

      const handleTimeUpdate = () => {
        if (audioRef.current) {
          setAudioProgress(audioRef.current.currentTime)
        }
      }

      const handleLoadedMeta = () => {
        if (audioRef.current) {
          setAudioDuration(audioRef.current.duration)
        }
      }

      audioRef.current.addEventListener('ended', handleTrackEnd)
      audioRef.current.addEventListener('timeupdate', handleTimeUpdate)
      audioRef.current.addEventListener('loadedmetadata', handleLoadedMeta)
      
      return () => {
        if (audioRef.current) {
          audioRef.current.removeEventListener('ended', handleTrackEnd)
          audioRef.current.removeEventListener('timeupdate', handleTimeUpdate)
          audioRef.current.removeEventListener('loadedmetadata', handleLoadedMeta)
          audioRef.current.pause()
          audioRef.current = null
          setIsPlaying(false)
        }
      }
    }
  }, [selectedMusicUrl, isMusicEnabled, isTimerRunning, useYoutube])

  // YouTube API lifecycle
  useEffect(() => {
    const { videoId, listId } = getYoutubeInfo(youtubeUrl)
    if (useYoutube && isMusicEnabled && (videoId || listId)) {
      const initYTPlayer = () => {
        if (!window.YT || !window.YT.Player) return
        
        try {
          ytPlayerRef.current = new window.YT.Player('global-yt-player', {
            events: {
              onReady: (event) => applyYoutubeVolume(event.target),
              onStateChange: (event) => {
                if (event.data === window.YT.PlayerState.PLAYING) {
                  setIsPlaying(true)
                  applyYoutubeVolume(event.target)
                  setAudioDuration(event.target.getDuration())
                  
                  const videoData = event.target.getVideoData()
                  if (videoData && videoData.title) {
                    setCurrentTrackName(videoData.title)
                    setCurrentTrackCover(`https://img.youtube.com/vi/${videoData.video_id}/mqdefault.jpg`)
                  }

                  const ytPlaylist = event.target.getPlaylist()
                  if (ytPlaylist) {
                    setPlaylistQueue(ytPlaylist.map((id, i) => ({ 
                      id, index: i, 
                      name: `YouTube Track ${i + 1}`,
                      cover: `https://img.youtube.com/vi/${id}/mqdefault.jpg` 
                    })))
                    setCurrentTrackIndex(event.target.getPlaylistIndex())
                  } else {
                    setPlaylistQueue([])
                  }
                  
                  if (ytIntervalRef.current) clearInterval(ytIntervalRef.current)
                  ytIntervalRef.current = setInterval(() => {
                    setAudioProgress(event.target.getCurrentTime())
                  }, 1000)
                } else if (event.data === window.YT.PlayerState.PAUSED || event.data === window.YT.PlayerState.ENDED) {
                  if (ytIntervalRef.current) clearInterval(ytIntervalRef.current)
                  setIsPlaying(false)
                }
              }
            }
          })
        } catch (e) {
          console.error("YouTube Player init error:", e)
        }
      }

      if (!window.YT) {
        const tag = document.createElement('script')
        tag.src = "https://www.youtube.com/iframe_api"
        const firstScriptTag = document.getElementsByTagName('script')[0]
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag)
        window.onYouTubeIframeAPIReady = initYTPlayer
      } else {
        // Wait a tick for iframe to mount
        setTimeout(initYTPlayer, 500)
      }

      return () => {
        if (ytIntervalRef.current) clearInterval(ytIntervalRef.current)
        if (ytPlayerRef.current && ytPlayerRef.current.destroy) {
          try { ytPlayerRef.current.destroy() } catch(e) {}
        }
        ytPlayerRef.current = null
      }
    }
  }, [useYoutube, isMusicEnabled, youtubeUrl])

  // Fetch YouTube titles in the background
  useEffect(() => {
    if (!useYoutube || playlistQueue.length === 0) return

    // Find up to 5 IDs that we haven't fetched yet
    const idsToFetch = playlistQueue
      .map(t => t.id)
      .filter(id => !ytTitles[id])
      .slice(0, 5)

    if (idsToFetch.length === 0) return

    let isCancelled = false

    const fetchFirstChunk = async () => {
      const promises = idsToFetch.map(id => 
        fetch(`https://noembed.com/embed?url=https://www.youtube.com/watch?v=${id}`)
          .then(res => res.json())
          .then(data => ({ id, title: data.title || `YouTube Track` }))
          .catch(() => ({ id, title: `YouTube Track` }))
      )
      
      const results = await Promise.all(promises)
      if (isCancelled) return
      
      const newTitles = {}
      results.forEach(r => { newTitles[r.id] = r.title })
      
      setYtTitles(prev => ({ ...prev, ...newTitles }))
    }

    const timer = setTimeout(fetchFirstChunk, 500)

    return () => {
      isCancelled = true
      clearTimeout(timer)
    }
  }, [playlistQueue, useYoutube, ytTitles])

  // Keep the latest volume in a ref so player callbacks created earlier still use it
  const volumeRef = useRef({ volume, isMuted })
  volumeRef.current = { volume, isMuted }

  function applyYoutubeVolume(player) {
    if (!player || typeof player.setVolume !== 'function') return
    try {
      const { volume: v, isMuted: m } = volumeRef.current
      player.setVolume(v)
      if (m || v === 0) player.mute()
      else player.unMute()
    } catch (e) {
      // the player can be mid-teardown; nothing to do
    }
  }

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = isMuted ? 0 : volume / 100
    applyYoutubeVolume(ytPlayerRef.current)
    try {
      localStorage.setItem('musicVolume', JSON.stringify(volume))
      localStorage.setItem('musicMuted', JSON.stringify(isMuted))
    } catch (e) {
      // storage may be unavailable (private mode); the volume still works for this session
    }
  }, [volume, isMuted])

  const setVolume = (v) => {
    const next = Math.min(100, Math.max(0, Math.round(Number(v) || 0)))
    setVolumeState(next)
    if (next > 0 && isMuted) setIsMuted(false) // dragging the slider up unmutes
  }
  const toggleMute = () => setIsMuted((m) => !m)

  // Sync shuffle and repeat with YT Player if it changes after init
  useEffect(() => {
    if (useYoutube && ytPlayerRef.current && typeof ytPlayerRef.current.setShuffle === 'function') {
      ytPlayerRef.current.setShuffle(isShuffle)
    }
  }, [isShuffle, useYoutube])

  useEffect(() => {
    if (useYoutube && ytPlayerRef.current && typeof ytPlayerRef.current.setLoop === 'function') {
      ytPlayerRef.current.setLoop(isRepeat)
    } else if (!useYoutube && audioRef.current) {
      audioRef.current.loop = isRepeat
    }
  }, [isRepeat, useYoutube])

  const startFocusTimer = async (taskIdObj = activeTaskId) => {
    // Prevent React synthetic events from being passed as taskId
    const taskId = (taskIdObj && typeof taskIdObj === 'object' && taskIdObj.nativeEvent) ? activeTaskId : (taskIdObj || activeTaskId)

    if (isTimerRunning) return

    setIsTimerRunning(true)
    setIsBreakTime(false)
    setCurrentTime(focusTimer * 60)
    
    if (taskId) setActiveTaskId(taskId)

    // Start music
    if (isMusicEnabled && !useYoutube && selectedMusicUrl) {
      try {
        if (audioRef.current) {
          audioRef.current.pause()
          audioRef.current.src = ''
          audioRef.current.load()
          audioRef.current = null
        }
        await new Promise((resolve) => setTimeout(resolve, 100))
        audioRef.current = new Audio(selectedMusicUrl)
        audioRef.current.loop = false
        audioRef.current.volume = isMuted ? 0 : volume / 100

        audioRef.current.addEventListener('ended', () => {
          if (isMusicEnabled) {
            if (isRepeat) {
              audioRef.current.currentTime = 0
              audioRef.current.play()
              return
            }
            let nextIndex = isShuffle ? Math.floor(Math.random() * FOCUS_MUSIC_URLS.length) : (currentTrackIndex + 1) % FOCUS_MUSIC_URLS.length
            const nextTrackItem = FOCUS_MUSIC_URLS[nextIndex]
            setSelectedMusicUrl(nextTrackItem.url)
            setCurrentTrackName(nextTrackItem.name)
            setCurrentTrackCover(null)
            setCurrentTrackIndex(nextIndex)
          }
        })

        const playPromise = audioRef.current.play()
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              setIsPlaying(true)
              console.log('Global music playing successfully')
            })
            .catch((err) => {
              console.error('Audio play failed:', err)
              setIsPlaying(false)
              toast.warning('Music playback blocked by browser - interact with page first')
            })
        }
      } catch (err) {
        console.error('Audio setup failed:', err)
      }
    }

    // Start tracking API
    if (taskId) {
      try {
        const token = localStorage.getItem('usertoken')
        const res = await axios.post(
          `${config.API_BASE_URL}/api/dailyplan/focus-session/start`,
          { task_id: taskId },
          { headers: { Authorization: `Bearer ${token}` } },
        )
        setCurrentSessionId(res.data.session_id)
      } catch (err) {
        console.error('Error starting focus session:', err)
      }
    }

    if (useYoutube && isMusicEnabled) {
      setIsPlaying(true)
    }

    timerRef.current = setInterval(() => {
      setCurrentTime((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current)
          handleTimerComplete()
          return 0
        }
        return prev - 1
      })
    }, 1000)

    toast.info('🎯 Focus session started!')
  }

  const handleTimerComplete = async () => {
    setIsTimerRunning(false)

    if (currentSessionId) {
      try {
        const token = localStorage.getItem('usertoken')
        await axios.post(
          `${config.API_BASE_URL}/api/dailyplan/focus-session/end`,
          { session_id: currentSessionId },
          { headers: { Authorization: `Bearer ${token}` } },
        )
        setCurrentSessionId(null)
      } catch (err) {
        console.error('Error ending focus session:', err)
      }
    }

    if (!isBreakTime) {
      toast.success('🎉 Focus session completed! Time for a break.')
      setIsBreakTime(true)
      setCurrentTime(breakTimer * 60)
      setIsTimerRunning(true)

      timerRef.current = setInterval(() => {
        setCurrentTime((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current)
            setIsTimerRunning(false)
            setIsBreakTime(false)
            toast.info('✨ Break time over! Ready for another session?')
            return 0
          }
          return prev - 1
        })
      }, 1000)
    } else {
      setIsBreakTime(false)
      toast.info('✨ Break time over! Ready for another session?')
    }
  }

  const stopFocusTimer = async () => {
    setIsTimerRunning(false)
    setIsBreakTime(false)
    if (timerRef.current) clearInterval(timerRef.current)
    setCurrentTime(0)
    setActiveTaskId(null)

    if (currentSessionId) {
      try {
        const token = localStorage.getItem('usertoken')
        await axios.post(
          `${config.API_BASE_URL}/api/dailyplan/focus-session/end`,
          { session_id: currentSessionId },
          { headers: { Authorization: `Bearer ${token}` } },
        )
        setCurrentSessionId(null)
      } catch (err) {
        console.error('Error ending focus session:', err)
      }
    }

    toast.info('Timer stopped')
  }

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  // Enhanced Controls
  const togglePlayPause = () => {
    if (!isMusicEnabled) return

    if (useYoutube) {
      const iframe = document.getElementById('global-yt-player')
      if (iframe) {
        const func = isPlaying ? 'pauseVideo' : 'playVideo'
        iframe.contentWindow.postMessage(JSON.stringify({ event: 'command', func }), '*')
        setIsPlaying(!isPlaying)
      }
    } else {
      if (audioRef.current) {
        if (isPlaying) {
          audioRef.current.pause()
        } else {
          audioRef.current.play()
        }
        setIsPlaying(!isPlaying)
      }
    }
  }

  const seekMusic = (time) => {
    if (useYoutube) {
      if (ytPlayerRef.current && typeof ytPlayerRef.current.seekTo === 'function') {
        ytPlayerRef.current.seekTo(time, true)
        setAudioProgress(time)
      }
    } else if (audioRef.current) {
      audioRef.current.currentTime = time
      setAudioProgress(time)
    }
  }

  const stopMusic = () => {
    if (useYoutube) {
      const iframe = document.getElementById('global-yt-player')
      if (iframe) iframe.contentWindow.postMessage(JSON.stringify({ event: 'command', func: 'stopVideo' }), '*')
    } else {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current.currentTime = 0
        setAudioProgress(0)
      }
    }
    setIsPlaying(false)
  }

  const toggleShuffle = () => setIsShuffle(!isShuffle)
  const toggleRepeat = () => setIsRepeat(!isRepeat)

  const playTrackAtIndex = (index) => {
    if (useYoutube) {
      if (ytPlayerRef.current && typeof ytPlayerRef.current.playVideoAt === 'function') {
        ytPlayerRef.current.playVideoAt(index)
      }
    } else {
      const item = FOCUS_MUSIC_URLS[index]
      if (item) {
        setSelectedMusicUrl(item.url)
        setCurrentTrackName(item.name)
        setCurrentTrackCover(null)
        setCurrentTrackIndex(index)
      }
    }
  }

  const nextTrack = () => {
    if (useYoutube) {
      if (ytPlayerRef.current && typeof ytPlayerRef.current.nextVideo === 'function') {
        ytPlayerRef.current.nextVideo()
      }
    } else {
      let nextIndex = isShuffle ? Math.floor(Math.random() * FOCUS_MUSIC_URLS.length) : (currentTrackIndex + 1) % FOCUS_MUSIC_URLS.length
      playTrackAtIndex(nextIndex)
    }
  }

  const prevTrack = () => {
    if (useYoutube) {
      if (ytPlayerRef.current && typeof ytPlayerRef.current.previousVideo === 'function') {
        ytPlayerRef.current.previousVideo()
      }
    } else {
      let prevIndex = isShuffle ? Math.floor(Math.random() * FOCUS_MUSIC_URLS.length) : (currentTrackIndex - 1 + FOCUS_MUSIC_URLS.length) % FOCUS_MUSIC_URLS.length
      playTrackAtIndex(prevIndex)
    }
  }

  const getIframeSrc = (url) => {
    const { videoId, listId } = getYoutubeInfo(url)
    if (listId && !videoId) {
      return `https://www.youtube.com/embed?listType=playlist&list=${listId}&autoplay=1&enablejsapi=1`
    }
    if (listId && videoId) {
      return `https://www.youtube.com/embed/${videoId}?list=${listId}&autoplay=1&enablejsapi=1`
    }
    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}?autoplay=1&loop=1&playlist=${videoId}&enablejsapi=1`
    }
    return ''
  }

  return (
    <FocusContext.Provider
      value={{
        focusTimer, setFocusTimer,
        breakTimer, setBreakTimer,
        isTimerRunning, isBreakTime, currentTime,
        activeTaskId, setActiveTaskId,
        currentSessionId,
        isMusicEnabled, setIsMusicEnabled,
        useYoutube, setUseYoutube,
        youtubeUrl, setYoutubeUrl,
        selectedMusicUrl, setSelectedMusicUrl,
        currentTrackName, setCurrentTrackName,
        currentTrackCover, setCurrentTrackCover,
        isPlaying, audioProgress, audioDuration,
        isShuffle, isRepeat, playlistQueue, currentTrackIndex, ytTitles,
        togglePlayPause, seekMusic, stopMusic,
        nextTrack, prevTrack, toggleShuffle, toggleRepeat, playTrackAtIndex,
        volume, setVolume, isMuted, toggleMute,
        startFocusTimer, stopFocusTimer, formatTime
      }}
    >
      {children}
      {/* Global hidden YouTube Player */}
      {isMusicEnabled && useYoutube && getIframeSrc(youtubeUrl) && (
        <iframe
          id="global-yt-player"
          width="1"
          height="1"
          src={getIframeSrc(youtubeUrl)}
          title="Global YouTube audio player"
          allow="autoplay"
          style={{ position: 'fixed', bottom: 0, opacity: 0, pointerEvents: 'none', zIndex: -9999 }}
        />
      )}
    </FocusContext.Provider>
  )
}

export const useFocus = () => useContext(FocusContext)
